/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import ifiLogo from './assets/ifi-logo.png';
import aimsLogo from './assets/aims-logo-250.png';
import { useStore } from './stores/common';
import * as Selector from './stores/selector';
import { setMessage, visitHomepage } from './helpers';
import MainMenu from './mainMenu';
import { BUILD_TIME_EST, ProjectType, SpaceshipDisplayMode, VERSION } from './constants';
import ShareLinks from './shareLinks';
import AcceptCookie from './acceptCookie';

import DropdownContextMenu from './components/contextMenu';
import { useTranslation } from 'react-i18next';
import Loading from './loading';
import KeyboardListener from './keyboardListener';
import { usePrimitiveStore } from './stores/commonPrimitive';
import ExportProgressOverlay from './project/exportProgressOverlay';
import CloudManager from './cloudManager';
import {
  AlertFilled,
  AppstoreOutlined,
  CloudTwoTone,
  ExperimentOutlined,
  LinkOutlined,
  ShareAltOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import SplitPane from './components/splitPane.tsx';
import { useRefStore } from './stores/commonRef.ts';
import { Badge, Button, Input, Popover, Segmented, Space } from 'antd';
import { Util } from './Util.ts';
import i18n from './i18n/i18n.ts';
import Messenger from './messengers.tsx';
import { useIsMobile, useIsPortrait } from './hooks.ts';
import { useCloudProjects } from './useCloudProjects';
import LandingPage from './landing/landingPage.tsx';
import { clearTemplateFromUrl, hasTemplate, readTemplateFromUrl, runTemplate } from './landing/caseStudyTemplates.ts';

// The two heavyweight panes. Between them they pull in every project type's
// viewer, gallery, chart and agent tool catalog — several megabytes that a
// visitor sitting on the landing page has no use for yet. The Suspense
// boundaries around their render sites already existed, so making them lazy
// is a drop-in. See prefetchAppShell below: we warm both chunks during idle
// time on the landing page, so entering a project is no slower than before —
// first paint just stops waiting on them.
const ProjectGallery = React.lazy(() => import('./project/projectGallery.tsx'));
const Lab = React.lazy(() => import('./lab'));

// Cockpit only mounts in flight/navigation mode, which most sessions never
// enter; the rest are modals gated behind a store flag that starts false.
// All of them were being compiled on every page load to render nothing.
const Cockpit = React.lazy(() => import('./view/cockpit.tsx'));
const PeriodicTable = React.lazy(() => import('./periodicTable.tsx'));
const SubmitToClassModal = React.lazy(() => import('./classroom/submitToClassModal'));
const ExportModelModal = React.lazy(() => import('./project/exportModelModal'));
const LessonPlanModal = React.lazy(() => import('./lessonPlans/lessonPlanModal.tsx'));
const LessonPlanListPanel = React.lazy(() => import('./lessonPlans/lessonPlanListPanel.tsx'));

// Warm the two big panes while the user is still reading the landing page, so
// the code is already parsed by the time they pick a project type. Failures are
// swallowed — this is an optimization, and the real import on render will
// surface any genuine load error through the Suspense boundary.
//
// Deliberately NOT requestIdleCallback. Measured at 6x CPU throttle (a low-end
// Chromebook), idle never arrived early enough: the panes are ~2.2 MB to fetch
// and parse, so a user who clicked a project tile within ~3 s hit the fetch
// cold and the lab took 6.8 s to appear — WORSE than before the split. Firing
// two frames after mount instead (i.e. just after first paint, so the landing
// still renders promptly) gives the fetch a head start and pulls that back to
// ~4 s. Both chunks are needed by every project type, so there is nothing
// speculative about warming them — the only question is when.
let appShellPrefetched = false;
const prefetchAppShell = () => {
  if (appShellPrefetched) return;
  appShellPrefetched = true;
  const warm = () => {
    import('./project/projectGallery.tsx').catch(() => {});
    import('./lab').catch(() => {});
  };
  if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') {
    setTimeout(warm, 0);
    return;
  }
  // Double rAF: the first fires before the paint that follows this commit, the
  // second after it. Starting the download any earlier would contend with the
  // landing's own render for the same single slow core.
  requestAnimationFrame(() => requestAnimationFrame(warm));
};

const LandscapeGate = React.memo(({ onContinue }: { onContinue: () => void }) => {
  // Subscribe to language so the gate re-renders when the user picks a
  // different locale (the gate appears before they reach the main app).
  const language = useStore(Selector.language);
  const lang = { lng: language };
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        // Layered radial highlights over near-black give the panel real depth
        // without looking flat. The cyan tint nods to the chamber gradient
        // without competing with the molecule renderings users see next.
        background:
          'radial-gradient(120% 80% at 50% -10%, rgba(64, 156, 255, 0.18) 0%, rgba(0, 0, 0, 0) 60%),' +
          'radial-gradient(80% 60% at 50% 110%, rgba(64, 156, 255, 0.10) 0%, rgba(0, 0, 0, 0) 60%),' +
          '#05070d',
        color: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '32px 24px',
        textAlign: 'center',
        userSelect: 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <style>
        {`@keyframes aims-phone-rock {
          0%, 12% { transform: rotate(0deg); }
          50%, 62% { transform: rotate(-90deg); }
          88%, 100% { transform: rotate(0deg); }
        }
        @keyframes aims-arrow-pulse {
          0%, 100% { opacity: 0.3; }
          45%, 55% { opacity: 1; }
        }`}
      </style>

      {/* top: brand */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', opacity: 0.95 }}>
        <img
          src={aimsLogo}
          alt="AIMS"
          width={48}
          height={48}
          style={{ filter: 'drop-shadow(0 2px 12px rgba(96, 165, 250, 0.35))' }}
        />
        <span
          style={{
            fontSize: '11px',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.55)',
          }}
        >
          {i18n.t('landscapeGate.Tagline', lang)}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.42)',
            marginTop: '-2px',
          }}
        >
          {i18n.t('name.IFI', lang)}
        </span>
      </div>

      {/* middle: animated rotation visual + headline */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: '160px',
            height: '160px',
            position: 'relative',
            marginBottom: '32px',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {/* glow ring */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 50% 50%, rgba(96, 165, 250, 0.22) 0%, rgba(96, 165, 250, 0) 65%)',
            }}
          />
          {/* curved arrow indicator */}
          <svg
            width="160"
            height="160"
            viewBox="0 0 160 160"
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              animation: 'aims-arrow-pulse 2.6s ease-in-out infinite',
            }}
          >
            <defs>
              <linearGradient id="arrowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#a78bfa" />
              </linearGradient>
            </defs>
            <path
              d="M 30 80 A 50 50 0 0 1 130 80"
              fill="none"
              stroke="url(#arrowGrad)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="4 6"
            />
            <path
              d="M 130 80 L 124 72 M 130 80 L 124 88"
              fill="none"
              stroke="#a78bfa"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
          {/* phone glyph */}
          <svg
            width="56"
            height="96"
            viewBox="0 0 56 96"
            aria-hidden
            style={{
              transformOrigin: '50% 50%',
              animation: 'aims-phone-rock 3.6s cubic-bezier(0.65, 0.0, 0.35, 1) infinite',
            }}
          >
            <rect
              x="2"
              y="2"
              width="52"
              height="92"
              rx="9"
              ry="9"
              fill="rgba(255, 255, 255, 0.04)"
              stroke="rgba(255, 255, 255, 0.85)"
              strokeWidth="2"
            />
            <rect x="6" y="10" width="44" height="72" rx="2" ry="2" fill="url(#screenGrad)" opacity="0.85" />
            <defs>
              <linearGradient id="screenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#1e293b" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            <circle cx="28" cy="88" r="2.2" fill="rgba(255, 255, 255, 0.75)" />
            <rect x="22" y="6" width="12" height="2" rx="1" fill="rgba(255, 255, 255, 0.6)" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: '26px',
            margin: '0 0 10px',
            color: '#ffffff',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {i18n.t('landscapeGate.Headline', lang)}
        </h1>
        <p
          style={{
            fontSize: '14.5px',
            lineHeight: 1.55,
            color: 'rgba(255, 255, 255, 0.62)',
            maxWidth: '300px',
            margin: 0,
          }}
        >
          {i18n.t('landscapeGate.Body', lang)}
        </p>
      </div>

      {/* bottom: action */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', width: '100%' }}>
        <Button
          size="large"
          onClick={onContinue}
          style={{
            minWidth: '240px',
            height: '48px',
            borderRadius: '10px',
            fontWeight: 500,
            background: 'rgba(255, 255, 255, 0.06)',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            color: '#ffffff',
            backdropFilter: 'blur(6px)',
          }}
        >
          {i18n.t('landscapeGate.ContinueInPortrait', lang)}
        </Button>
        <span style={{ fontSize: '11.5px', color: 'rgba(255, 255, 255, 0.4)', letterSpacing: '0.02em' }}>
          {i18n.t('landscapeGate.AutoDismissHint', lang)}
        </span>
      </div>
    </div>
  );
});

const AppCreator = React.memo(({ viewOnly = false }: { viewOnly: boolean }) => {
  const setCommonStore = useStore(Selector.set);
  const user = useStore(Selector.user);
  const latestVersion = usePrimitiveStore(Selector.latestVersion);
  // See PrimitiveStoreState.deepLinkPending: hold the gallery pane back until
  // the linked project's type is known.
  const deepLinkPending = usePrimitiveStore(Selector.deepLinkPending);
  const loggable = useStore.getState().loggable;
  const language = useStore(Selector.language);
  const hideGallery = useStore(Selector.hideGallery);
  const chamberViewerPercentWidth = useStore(Selector.chamberViewerPercentWidth);
  const projectType = useStore(Selector.projectType);
  const projectOwner = useStore(Selector.projectOwner);
  const projectTitle = useStore(Selector.projectTitle);
  const loadChemicalElements = useStore(Selector.loadChemicalElements);
  const loadProvidedMolecularProperties = useStore(Selector.loadProvidedMolecularProperties);
  const showSubmitToClassModal = usePrimitiveStore(Selector.showSubmitToClassModal);
  const showExportModelModal = usePrimitiveStore(Selector.showExportModelModal);
  const changed = usePrimitiveStore(Selector.changed);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const spaceshipDisplayMode = useStore(Selector.spaceshipDisplayMode);
  const navigationView = useStore(Selector.navigationView);
  const showPeriodicTable = usePrimitiveStore(Selector.showPeriodicTable);
  const showLanding = usePrimitiveStore(Selector.showLanding);
  const showLessonPlanModal = usePrimitiveStore(Selector.showLessonPlanModal);
  const showLessonPlanListPanel = usePrimitiveStore(Selector.showLessonPlanListPanel);

  // const setSkipChange = usePrimitiveStore(Selector.setSkipChange);
  // const cameraPosition = useStore(Selector.cameraPosition);
  // const panCenter = useStore(Selector.panCenter);
  // useEffect(() => {
  //   if (usePrimitiveStore.getState().skipChange) {
  //     setSkipChange(false);
  //   } else if (!usePrimitiveStore.getState().changed) {
  //     setChanged(true);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [cameraPosition, panCenter]);

  const [latestVersionReminder, setLatestVersionReminder] = useState<boolean>(false);

  // Inline rename of the open project straight from the title shown in the
  // header. Reuses useCloudProjects.renameProject, which migrates the
  // Firestore doc (and the public index, if published) and updates the store.
  const { renameProject } = useCloudProjects();
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const [titleDraft, setTitleDraft] = useState<string>('');
  // Enter fires onPressEnter and then onBlur — guard against committing twice
  // (the second pass would hit a non-existent old doc and emit a stray toast).
  const committingTitleRef = useRef<boolean>(false);

  const beginEditTitle = () => {
    if (viewOnly || !isOwner || !projectTitle) return;
    committingTitleRef.current = false;
    setTitleDraft(projectTitle);
    setEditingTitle(true);
  };

  const commitTitleRename = () => {
    if (committingTitleRef.current) return;
    committingTitleRef.current = true;
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (projectTitle && next && next !== projectTitle) {
      renameProject(projectTitle, next);
    }
  };

  const cancelTitleRename = () => {
    committingTitleRef.current = true; // suppress the trailing onBlur commit
    setEditingTitle(false);
  };

  const isMobile = useIsMobile();
  const isPortrait = useIsPortrait();
  const [mobileTab, setMobileTab] = useState<'gallery' | 'chamber'>('gallery');
  // Sticky for the session: once the user opts in to portrait, don't keep
  // nagging them on every rotation back to portrait or on a page refresh.
  const [portraitDismissed, setPortraitDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem('aims.portraitDismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismissPortraitGate = () => {
    setPortraitDismissed(true);
    try {
      window.sessionStorage.setItem('aims.portraitDismissed', '1');
    } catch {
      /* ignore — private mode etc. */
    }
  };
  // Suppress the "rotate to landscape" overlay in view-only mode (shared
  // public links, embed previews) — viewers landed on a curated link and
  // shouldn't be greeted by a chrome interrupt suggesting a different layout.
  const showLandscapeGate = isMobile && isPortrait && !portraitDismissed && !viewOnly;

  const firstLoadRef = useRef<boolean>(true);

  useEffect(() => {
    if (latestVersion) setLatestVersionReminder(VERSION.localeCompare(latestVersion) < 0);
  }, [latestVersion]);

  useEffect(() => {
    if (firstLoadRef.current) {
      // Case-study template handoff: if the user clicked "Try this idea"
      // on the standalone /case-studies page, the navigation here carries
      // ?template=<id>. Run the template setup, then drop the param so a
      // refresh doesn't re-trigger it. The loader itself dismisses the
      // landing — see runTemplate.
      const templateId = readTemplateFromUrl();
      if (templateId && hasTemplate(templateId)) {
        runTemplate(templateId).finally(() => clearTemplateFromUrl());
      } else if (templateId) {
        // Unknown ID — drop it to keep the URL bar clean.
        clearTemplateFromUrl();
      }
      loadChemicalElements();
      loadProvidedMolecularProperties();
      setCommonStore((state) => {
        // make sure the selected molecule is in the same array of molecules
        if (state.projectState.selectedMolecule) {
          for (const m of state.projectState.molecules) {
            if (m.name === state.projectState.selectedMolecule.name) {
              state.projectState.selectedMolecule = m;
              break;
            }
          }
        }
        // make sure the test molecule is in the same array of molecules
        if (state.projectState.ligand) {
          for (const m of state.projectState.molecules) {
            if (m.name === state.projectState.ligand.name) {
              state.projectState.ligand = m;
              break;
            }
          }
        }
      });
      firstLoadRef.current = false;
    }
    // eslint-disable-next-line
  }, []);

  // Start fetching the gallery/lab chunks as soon as the shell is up. On the
  // landing page this happens while the user reads the project-type tiles; for
  // a returning user whose project restores straight into the app, the render
  // below has already requested the same modules, and import() dedupes.
  useEffect(() => {
    prefetchAppShell();
  }, []);

  const { t } = useTranslation();
  const lang = useMemo(() => {
    return { lng: language };
  }, [language]);

  const setNavigationView = (selected: boolean) => {
    setCommonStore((state) => {
      state.projectState.navigationView = selected;
      if (selected) {
        state.projectState.showInstructionPanel = false;
        state.projectState.spaceshipDisplayMode = SpaceshipDisplayMode.INSIDE_VIEW;
      } else {
        state.projectState.spaceshipDisplayMode = SpaceshipDisplayMode.NONE;
      }
    });
    usePrimitiveStore.getState().set((state) => {
      state.enableRotate = !selected;
    });
  };

  const isOwner = user.uid && user.uid === projectOwner;

  // The compact mobile chrome (96px header with the Gallery/Chamber tab
  // toggle) is for portrait phones only. In landscape, the screen is wide
  // enough that the desktop split-pane layout fits — and that's the layout
  // users expect for drag-drop and side-by-side comparison.
  const useMobileLayout = isMobile && isPortrait;
  const headerHeight = useMobileLayout ? 96 : 72;

  if (showLandscapeGate) {
    return <LandscapeGate onContinue={dismissPortraitGate} />;
  }

  // Show the four-tile landing whenever showLanding is true. Default is true
  // (set in the primitive store), but the firstLoad effect below flips it
  // off when a persisted project is restored — we don't want to interrupt a
  // returning user with the picker. The Help → Welcome menu item flips it
  // back on so users can revisit the landing on demand regardless of state.
  // Suppressed in view-only mode (shared deep links land on the project).
  if (showLanding && !viewOnly) {
    return <LandingPage />;
  }

  return (
    <div className="App" id={'whole-app'}>
      {useMobileLayout ? (
        <div
          style={{
            backgroundColor: 'lightblue',
            height: '96px',
            display: 'flex',
            flexDirection: 'column',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // Right side reserves room for the floating MainToolBar
              // (Examples icon + divider + avatar / Sign In on mobile;
              // adds Commons + Tutorial icons on desktop) which sits
              // absolutely at right:10px. ~140px covers it with the Save
              // pill having clearance.
              padding: '0 140px 0 56px',
            }}
          >
            <Badge
              offset={['8px', '0px']}
              count={
                latestVersionReminder ? (
                  <AlertFilled
                    style={{ color: 'red', cursor: 'pointer' }}
                    title={t('message.NewVersionAvailable', lang)}
                  />
                ) : undefined
              }
            >
              <span
                onClick={visitHomepage}
                style={{
                  cursor: 'pointer',
                  // 16px (was 20px) keeps the title compact on narrow phones so
                  // it doesn't crowd the chamber-area overlays (binding-score
                  // panel etc.) sitting just below the header.
                  fontSize: '16px',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title={projectTitle ?? 'Artificial Intelligence for Molecular Sciences (AIMS)'}
              >
                {`${t('name.AIMS', lang)}`}
                {!viewOnly && projectTitle && isOwner && changed && (
                  <span
                    aria-label="unsaved changes"
                    title={t('menu.project.SaveProject', lang)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#fa8c16',
                      display: 'inline-block',
                    }}
                  />
                )}
              </span>
            </Badge>
            <div style={{ flex: 1 }} />
            {!viewOnly && projectTitle && isOwner && changed && (
              <Button
                type="primary"
                size="small"
                title={t('menu.project.SaveProject', lang)}
                onClick={() => {
                  usePrimitiveStore.getState().setSaveProjectFlag(true);
                  if (loggable) {
                    setCommonStore((state) => {
                      state.actionInfo = {
                        name: 'Save Project',
                        timestamp: new Date().getTime(),
                      };
                    });
                  }
                }}
              >
                {t('word.Save', lang)}
              </Button>
            )}
          </div>
          {/* Hide the gallery/chamber toggle in view-only mode when the
              project has hidden the gallery — viewers should land directly
              on the chamber and not see a "Gallery" tab that would render
              an empty / suppressed view. */}
          {!(viewOnly && hideGallery) && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '4px' }}>
              <Segmented
                size="small"
                value={mobileTab}
                onChange={(v) => setMobileTab(v as 'gallery' | 'chamber')}
                options={[
                  { label: t('experiment.Gallery', lang), value: 'gallery', icon: <AppstoreOutlined /> },
                  { label: t('experiment.Chamber', lang), value: 'chamber', icon: <ExperimentOutlined /> },
                ]}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: 'lightblue',
            height: '72px',
            paddingTop: '10px',
            textAlign: 'start',
            userSelect: 'none',
          }}
        >
          <Badge
            offset={['10px', '0px']}
            count={
              latestVersionReminder ? (
                <AlertFilled
                  style={{ color: 'red', cursor: 'pointer' }}
                  title={t('message.NewVersionAvailable', lang)}
                />
              ) : undefined
            }
          >
            <Space
              style={{
                marginLeft: '90px',
                verticalAlign: 'middle',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '30px',
              }}
              title={'Artificial Intelligence for Molecular Sciences (AIMS)'}
              onClick={visitHomepage}
            >
              {`${t('name.AIMS', lang)}`}
            </Space>
          </Badge>
          {projectTitle && (
            <CloudTwoTone
              twoToneColor={isOwner ? 'blue' : 'gray'}
              style={{
                paddingLeft: '24px',
                fontSize: '18px',
                verticalAlign: 'middle',
                position: 'relative',
                top: '4px',
              }}
            />
          )}
          <Space
            style={{
              paddingLeft: '4px',
              fontSize: '16px',
              verticalAlign: 'bottom',
              userSelect: 'none',
              color: isOwner ? 'black' : 'gray',
            }}
            title={projectTitle ?? t('projectPanel.ProjectTitle', lang)}
          >
            {/* Truncate long project titles so they don't run under the
                MainToolBar on narrow viewports — e.g. a phone in
                landscape, which uses this desktop chrome. Reserve ~360px
                for the AIMS logo + cloud icon on the left, ~120px for
                the Save pill and Share popover on the right (when shown),
                and ~180px for the floating MainToolBar. Floor of 80px so
                a few characters remain visible even on very narrow widths. */}
            {editingTitle && !viewOnly && isOwner ? (
              <Input
                autoFocus
                size="small"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onPressEnter={commitTitleRename}
                onBlur={commitTitleRename}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') cancelTitleRename();
                }}
                style={{
                  display: 'inline-block',
                  width: 'max(120px, calc(100vw - 660px))',
                  fontSize: '16px',
                  verticalAlign: 'bottom',
                }}
              />
            ) : (
              <span
                onClick={beginEditTitle}
                title={!viewOnly && isOwner ? t('projectPanel.ClickToRenameProject', lang) : projectTitle ?? undefined}
                style={{
                  display: 'inline-block',
                  maxWidth: 'max(80px, calc(100vw - 660px))',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                  cursor: !viewOnly && isOwner ? 'text' : 'default',
                }}
              >
                {projectTitle ? projectTitle + (!viewOnly && isOwner && changed ? ' *' : '') : ''}
              </span>
            )}
            {!viewOnly && isOwner && changed && (
              <Button
                type="primary"
                size={'small'}
                style={{ marginLeft: '10px' }}
                title={t('menu.project.SaveProject', lang)}
                onClick={() => {
                  usePrimitiveStore.getState().setSaveProjectFlag(true);
                  if (loggable) {
                    setCommonStore((state) => {
                      state.actionInfo = {
                        name: 'Save Project',
                        timestamp: new Date().getTime(),
                      };
                    });
                  }
                }}
              >
                {t('word.Save', lang)}
              </Button>
            )}
            {!viewOnly && projectTitle && (
              <Popover
                content={
                  <Space direction={'vertical'}>
                    <Space direction={'horizontal'}>
                      <LinkOutlined style={{ fontSize: '20px' }} />
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (!user.uid || !projectTitle) return;
                          Util.generateProjectLink(user.uid, projectTitle, () => {
                            setMessage(
                              'success',
                              i18n.t('projectListPanel.ProjectLinkGeneratedInClipBoard', lang) + '.',
                            );
                          });
                        }}
                      >
                        {t('word.CopyLink', lang)}
                      </span>
                    </Space>
                    <Space direction={'vertical'}>
                      <Space direction={'horizontal'}>
                        <ShareAltOutlined style={{ fontSize: '20px' }} />
                        <span>{t('word.ShareVia', lang)}</span>
                      </Space>
                      <ShareLinks size={36} round={true} margin={'8px'} style={{ paddingLeft: '24px' }} />
                    </Space>
                  </Space>
                }
              >
                <UploadOutlined
                  title={t('word.Share', lang)}
                  style={{ paddingLeft: '10px', fontSize: '20px', color: 'navy', cursor: 'pointer' }}
                />
              </Popover>
            )}
          </Space>
        </div>
      )}

      {isMobile &&
        (() => {
          // Decide whether the footer is sitting on a dark chamber surface
          // or a light gallery surface, so the IFI logo + copyright stays
          // legible against either. Matches the desktop pattern where the
          // footer flips to antiquewhite on dark backgrounds.
          const onDarkBackground = useMobileLayout ? mobileTab === 'chamber' : hideGallery;
          return (
            <div
              style={{
                position: 'fixed',
                bottom: '2px',
                // Left-align in view-only mode (matches the desktop view-only
                // footer); center it for the regular app where there's no
                // chamber chrome at the bottom-left to compete with.
                left: viewOnly ? '6px' : 0,
                right: viewOnly ? 'auto' : 0,
                zIndex: 999,
                display: 'flex',
                justifyContent: viewOnly ? 'flex-start' : 'center',
                alignItems: 'center',
                gap: '4px',
                fontSize: '9px',
                lineHeight: 1.2,
                userSelect: 'none',
                pointerEvents: 'none',
                color: onDarkBackground ? 'rgba(255, 255, 255, 0.78)' : 'rgba(60, 60, 60, 0.7)',
                textShadow: onDarkBackground ? '0 0 3px rgba(0, 0, 0, 0.85)' : '0 0 2px rgba(255, 255, 255, 0.6)',
              }}
            >
              <img alt="IFI" src={ifiLogo} height={14} style={{ verticalAlign: 'middle' }} />
              <span>
                &copy;{new Date().getFullYear()} · {t('word.Built', lang)} {BUILD_TIME_EST}
              </span>
            </div>
          );
        })()}

      {isMobile ? null : viewOnly ? (
        <div
          style={{
            position: 'absolute',
            bottom: '6px',
            left: '6px',
            zIndex: 999,
            fontSize: '8px',
            userSelect: 'none',
            color: hideGallery ? 'rgba(255, 255, 255, 0.78)' : 'dimgray',
            textShadow: hideGallery ? '0 0 3px rgba(0, 0, 0, 0.85)' : 'none',
          }}
        >
          <img alt="IFI Logo" src={ifiLogo} height="16px" style={{ verticalAlign: 'middle' }} />
          {' ' + t('word.Built', lang) + ' ' + BUILD_TIME_EST}
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            bottom: '4px',
            left: '6px',
            zIndex: 999,
            display: 'flex',
            // Bottom-align so the copyright text sits 4px above the page
            // bottom instead of floating mid-height beside the taller logo.
            alignItems: 'flex-end',
            gap: '6px',
          }}
        >
          <img
            alt="IFI Logo"
            src={ifiLogo}
            height={hideGallery ? '28px' : '16px'}
            style={{
              userSelect: 'none',
            }}
          />
          <div
            style={{
              fontSize: '10px',
              lineHeight: 1,
              userSelect: 'none',
              color: hideGallery ? '#ffffff' : 'dimgray',
              textShadow: hideGallery ? '0 0 3px rgba(0, 0, 0, 0.85)' : 'none',
            }}
          >
            &copy;{new Date().getFullYear()} {`${t('name.IFI', lang)}`}.{' '}
            {t('word.Built', lang) + ' ' + BUILD_TIME_EST + '.'}
          </div>
        </div>
      )}

      <MainMenu viewOnly={viewOnly} isMobile={useMobileLayout} />
      <DropdownContextMenu>
        {/* must specify the height here for the floating window to have correct boundary check*/}
        <div
          style={{
            // 100dvh on mobile so the chamber fits *inside* the visible
            // viewport (URL bar / nav bar are subtracted dynamically). 100vh
            // would over-shoot on iOS/Android and force the page to scroll.
            height: useMobileLayout ? `calc(100dvh - ${headerHeight}px)` : `calc(100vh - ${headerHeight}px)`,
          }}
        >
          {useMobileLayout ? (
            <div style={{ height: '100%', position: 'relative' }}>
              {/* In view-only mode where the project has hidden the gallery,
                  force the chamber view regardless of the toggle's last
                  selection — the toggle itself is hidden above. */}
              {viewOnly && hideGallery ? (
                <Suspense fallback={<Loading />}>
                  <Lab />
                </Suspense>
              ) : mobileTab === 'gallery' ? (
                deepLinkPending ? null : (
                  <Suspense fallback={<Loading />}>
                    <ProjectGallery relativeWidth={1} />
                  </Suspense>
                )
              ) : (
                <Suspense fallback={<Loading />}>
                  <Lab />
                </Suspense>
              )}
            </div>
          ) : (
            <SplitPane
              hideGallery={!hideGallery}
              maxWidth={100}
              restoreKey={projectTitle ?? ''}
              defaultSize={!hideGallery ? 100 - chamberViewerPercentWidth : 0}
              onChange={(size) => {
                setCommonStore((state) => {
                  state.projectState.chamberViewerPercentWidth = 100 - size;
                });
                useRefStore.getState().resizeCanvases(size);
                setChanged(true);
              }}
            >
              {!hideGallery && !deepLinkPending ? (
                <Suspense fallback={<Loading />}>
                  <ProjectGallery relativeWidth={1 - chamberViewerPercentWidth * 0.01} />
                </Suspense>
              ) : (
                <></>
              )}
              <Suspense fallback={<Loading />}>
                <Lab />
              </Suspense>
            </SplitPane>
          )}
          <Suspense fallback={null}>
            {projectType === ProjectType.DRUG_DISCOVERY &&
              spaceshipDisplayMode === SpaceshipDisplayMode.INSIDE_VIEW && <Cockpit />}
            {projectType === ProjectType.MOLECULAR_MODELING && navigationView && <Cockpit />}
          </Suspense>
          <KeyboardListener setNavigationView={setNavigationView} />
        </div>
      </DropdownContextMenu>
      <CloudManager viewOnly={viewOnly} />
      {/* Every child here is gated behind a flag that starts false, so the
          fallback is never seen in practice — the chunk fetch overlaps the
          modal's own open animation. null keeps a spinner from flashing over
          the app in the rare slow-network case. */}
      <Suspense fallback={null}>
        {showSubmitToClassModal && <SubmitToClassModal />}
        {showLessonPlanModal && <LessonPlanModal />}
        {showLessonPlanListPanel && <LessonPlanListPanel />}
        {showExportModelModal && <ExportModelModal />}
        {showPeriodicTable && (
          <PeriodicTable
            close={() => {
              usePrimitiveStore.getState().set((state) => {
                state.showPeriodicTable = false;
              });
            }}
          />
        )}
      </Suspense>
      <ExportProgressOverlay />
      {!viewOnly && <AcceptCookie />}
      <Messenger />
    </div>
  );
});

export default AppCreator;
